import { jsx } from 'react/jsx-runtime';
import { createContext, forwardRef, useState, useMemo, useContext, useLayoutEffect } from 'react';
import { useForwardFocusToInput } from '../hooks/use-forward-focus-to-input.js';
import { useInteractionHandler } from '../hooks/use-interaction-handler.js';
import { useSharedRef } from '../hooks/use-shared-ref.js';
import { useSelectOnAutoFocus } from './input-primitive.hooks.js';

const InputContext = createContext(null);
/**
 * Allows placing elements "inside" the input. Must contain a single `InputPrimitive.Field` element.
 *
 * @see https://fpl.figma.design/primitives/input/
 */
const Root = forwardRef(({ onPointerDown: onPointerDownIn, spellCheck = false, ...props }, ref) => {
    const [disabled, setDisabled] = useState(false);
    const ctx = useMemo(() => ({ setDisabled }), []);
    const onPointerDown = useForwardFocusToInput(onPointerDownIn);
    return (jsx(InputContext.Provider, { value: ctx, children: jsx("div", { "data-fpl-component": "primitive", ...props, ref: ref, onPointerDown: onPointerDown, spellCheck: spellCheck, "data-fpl-disabled": disabled || undefined }) }));
});
Root.displayName = 'InputPrimitive.Root';
/**
 * A simple wrapper over a native text input element.
 *
 * @note This input is for text only, and restricts the `type` prop as such.
 *   FPL provides bespoke components for range (SliderPrimitive),
 *   checkbox (CheckboxPrimitive), and radio (RadioPrimitive).
 */
const InputPrimitive = Object.assign(
// False positive because of the Object.assign
// eslint-disable-next-line react/display-name
forwardRef(({ type = 'text', onChange, disabled, autoComplete = 'off', readOnly, spellCheck = false, recordingKey, ...props }, refIn) => {
    const ctx = useContext(InputContext);
    const ref = useSharedRef(refIn);
    useSelectOnAutoFocus(ref, props.autoFocus);
    const recordedOnChange = useInteractionHandler(onChange, { eventName: 'change', recordingKey }, [onChange]);
    useLayoutEffect(() => ctx?.setDisabled(disabled ?? false), [ctx, disabled]);
    return (jsx("input", { "data-fpl-component": "primitive", ...props, ref: ref, autoComplete: autoComplete, 
        // size acts as a hidden min-width for the input, but we'd like
        // inputs to be able to shrink if they have to
        size: 1, type: type, onChange: recordedOnChange ? (e) => recordedOnChange(e.target.value, { event: e }) : undefined, 
        // Instead of disabling the input, we mark it readonly so that it can still be focused.
        readOnly: disabled || readOnly, "aria-readonly": readOnly || undefined, "aria-disabled": disabled || undefined, spellCheck: spellCheck, "data-1p-ignore": type !== 'password' || undefined }));
}), { Root });
InputPrimitive.displayName = 'InputPrimitive';

export { InputPrimitive };
//# sourceMappingURL=input-primitive.js.map
